/**
 * Encounter URL Utilities
 *
 * Helper functions for computing encounter URLs based on status.
 * Used for deep linking and smart redirects.
 */

import { Encounter } from '@/types/encounter';

/**
 * The visit already has a recording, so the recorder does not open for it: a second recording would
 * be transcribed and billed again, and the server refuses it (409 VISIT_ALREADY_RECORDED). A failed
 * transcription leaves the recorder open, since the review page sends the doctor back to it. Works
 * for encounters and appointments alike.
 *
 * `alreadyRecorded` is the server's answer, from the visit's recordings, transcript and statuses,
 * so visits whose statuses were never kept up to date are covered. An API that predates it does not
 * send it, and the statuses decide.
 */
export function isAlreadyRecorded(visit: {
  alreadyRecorded?: boolean;
  recording?: { status?: string };
  transcription?: { status?: string };
}): boolean {
  if (typeof visit.alreadyRecorded === 'boolean') return visit.alreadyRecorded;
  if (visit.transcription?.status === 'failed') return false;
  return visit.transcription?.status === 'completed' || visit.recording?.status === 'completed';
}

/**
 * Compute the appropriate URL for an encounter based on its status
 *
 * @param encounter - The encounter object with recording/transcription status
 * @returns The URL path to navigate to (recording or validation page)
 */
export function computeEncounterUrl(encounter: Encounter): string {
  const encounterId = encounter.id;

  const recordingStatus = encounter.recording?.status;

  // If no recording or recording pending/failed → go to recording page
  if (!recordingStatus || recordingStatus === 'pending' || recordingStatus === 'failed') {
    return `/recording?encounterId=${encounterId}`;
  }

  // If recording completed → go to validation (regardless of transcription status)
  // The validation page handles showing loading state while transcription is processing
  if (recordingStatus === 'completed') {
    return `/encounter/${encounterId}/validation`;
  }

  // If currently recording → go to recording page to continue/finish
  if (recordingStatus === 'recording') {
    return `/recording?encounterId=${encounterId}`;
  }

  // Default: go to validation page
  return `/encounter/${encounterId}/validation`;
}
